import {Polyline} from "@react-google-maps/api";
import React, {useEffect, useState} from "react";
import {RouteGeometry, RouteResult} from "@ots-frontend/ots-data";
import {decode} from "@googlemaps/polyline-codec";
import PolylineOptions = google.maps.PolylineOptions;

const routingColors = [
  "#2F6DF2", // vivid blue (anchor)
  "#2FCFA2", // turquoise
  "#42B57F", // green-blue (anchor)
  "#5A4BF2", // indigo / blue-purple
  "#7C5BF2", // softer violet-blue
];

export function routingColorByIndex(i: number): string {
  return routingColors[i % routingColors.length];
}

export const toPath = (geom?: RouteGeometry): google.maps.LatLngLiteral[] => {
  if (!geom) {
    return [];
  }

  if (geom.coordinates && geom.coordinates.length) {
    return geom.coordinates.map((crd) => {
      return {lat: crd.lat, lng: crd.lon};
    });
  }

  if (geom.encodedPolyline) {
    return decode(geom.encodedPolyline, 5).map((tuple => {
      return {lat: tuple[0], lng: tuple[1]};
    }));
  }

  return [];
}

export type RoutingPolylineProps = Omit<PolylineOptions, 'clickable' | 'strokeOpacity' | 'strokeWeight' | 'zIndex'> & {
  id: string,
  route: RouteResult,
  onFitBounds?: (bounds: google.maps.LatLngBoundsLiteral) => void;
}

export type RouteGeometryPolylineProps = Omit<PolylineOptions, 'clickable' | 'strokeOpacity' | 'strokeWeight' | 'zIndex'> & {
  id: string,
  geometry?: RouteGeometry,
}

export const RouteGeometryPolyline = (props: RouteGeometryPolylineProps) => {
  const {
    id,
    geometry,
    ...rest
  } = props;

  const [hover, setHover] = useState(false);

  const path = React.useMemo(() => {
    return toPath(geometry)
  }, [geometry]);

  if (!path.length) {
    return null;
  }

  return (
    <Polyline
      key={id}
      path={path}
      options={{
        ...rest,
        strokeOpacity: hover ? 1 : 0.75,
        strokeWeight: hover ? 8 : 4,
        zIndex: hover ? 1000 : 0,
      }}
      onLoad={(polyline) => {
        polyline.addListener("mouseover", () => setHover(true));
        polyline.addListener("mouseout", () => setHover(false));
      }}
    />
  );
}

export const RoutingPolyline = (props: RoutingPolylineProps) => {
  const {
    id,
    route,
    onFitBounds,
    ...rest} = props;

  useEffect(() => {
    if (onFitBounds && route.bbox) {
      onFitBounds({
        south: route.bbox.south,
        west: route.bbox.west,
        north: route.bbox.north,
        east: route.bbox.east,
      });
    }
  }, [onFitBounds, route.bbox]);

  return (
    <RouteGeometryPolyline
      id={id}
      geometry={route.geometry}
      {...rest}/>
  );
};
